import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiStar } from 'react-icons/fi';
import DynamicFormViewer from '../../Components/FormData/DynamicFormViewer';
import { Button, Input, Modal } from '../../Components/UI';
import {
  getAllSubmitYourTestimonials,
  deleteSubmitYourTestimonial,
  updateSubmitYourTestimonialById,
  createPanelTestimonial,
} from '../../Redux/Store/API/FormsData/SubmitYourTestimonial';

const EMPTY_FORM = { name: '', location: '', rating: 5, feedback: '', showOnWebsite: true };

export default function TestimonialFormData() {
  const dispatch = useDispatch();
  // showOnWebsite values confirmed by the server since the list was last fetched,
  // so a toggle shows immediately without resetting the viewer's page/filters.
  const [websiteOverrides, setWebsiteOverrides] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  // Bumped after an add to remount the viewer, which refetches the list.
  const [listKey, setListKey] = useState(0);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const isShown = (row) => {
    const id = row?._id || row?.id;
    return id in websiteOverrides ? websiteOverrides[id] : Boolean(row?.showOnWebsite);
  };

  const handleToggleWebsite = async (row) => {
    const id = row?._id || row?.id;
    if (!id) return;
    const next = !isShown(row);
    setTogglingId(id);
    try {
      const saved = await dispatch(
        updateSubmitYourTestimonialById({ id, _id: id, showOnWebsite: next })
      ).unwrap();
      const savedValue = Boolean(saved?.showOnWebsite);
      setWebsiteOverrides((prev) => ({ ...prev, [id]: savedValue }));
      if (savedValue !== next) {
        toast.error('Could not change website visibility. Please log in again.');
      } else {
        toast.success(next ? 'Testimonial is now shown on the website' : 'Testimonial hidden from the website');
      }
    } catch (err) {
      toast.error('Failed to update website visibility');
    } finally {
      setTogglingId(null);
    }
  };

  const closeAdd = () => {
    if (isSaving) return;
    setIsAddOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.feedback.trim()) {
      toast.error('Name and feedback are required');
      return;
    }
    setIsSaving(true);
    try {
      await dispatch(
        createPanelTestimonial({
          name: form.name.trim(),
          location: form.location.trim(),
          rating: Number(form.rating),
          feedback: form.feedback.trim(),
          showOnWebsite: form.showOnWebsite,
        })
      ).unwrap();
      toast.success('Testimonial added');
      setIsAddOpen(false);
      setForm(EMPTY_FORM);
      setWebsiteOverrides({});
      setListKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.message || 'Failed to add testimonial');
    } finally {
      setIsSaving(false);
    }
  };

  const headers = [
    { key: 'firstname', label: 'First Name' },
    { key: 'lastname', label: 'Last Name' },
    { key: 'mobileNo', label: 'Mobile No' },
    { key: 'location', label: 'Location', render: (v) => v || '—' },
    { key: 'subject', label: 'Subject' },
    { key: 'feedback', label: 'Feedback / Review' },
    { key: 'rating', label: 'Rating' },
    {
      key: 'showOnWebsite',
      label: 'Show on website',
      render: (_v, row) => {
        const on = isShown(row);
        const busy = togglingId === (row?._id || row?.id);
        return (
          <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleWebsite(row);
            }}
            title={on ? 'Shown on website. Click to hide' : 'Hidden from website. Click to show'}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition ${
              on ? 'bg-emerald-500 border-emerald-500' : 'bg-slate-200 border-slate-300'
            } ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                on ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        );
      },
    },
    { key: 'sourcePage', label: 'Source Page', render: (v) => v || '—' },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-end px-7 md:px-9 pt-3">
        <Button size="sm" onClick={() => setIsAddOpen(true)}>
          <span className="flex items-center gap-1.5">
            <FiPlus className="w-4 h-4" /> Add testimonial
          </span>
        </Button>
      </div>

      <DynamicFormViewer
        key={listKey}
        title="Submitted Testimonials"
        headers={headers}
        fetchAction={getAllSubmitYourTestimonials}
        deleteAction={deleteSubmitYourTestimonial}
        updateAction={updateSubmitYourTestimonialById}
        stateSliceName="submitYourTestimonial"
        dataKey="getallSubmitYourTestimonials"
      />

      <Modal
        isOpen={isAddOpen}
        onClose={closeAdd}
        title="Add testimonial"
        subtitle="Enter a patient testimonial collected by the clinic"
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={closeAdd} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} isLoading={isSaving}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={handleAdd} className="space-y-3">
          <Input
            name="name"
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Patient name"
          />
          <Input
            name="location"
            label="Location"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="e.g. Pune"
          />
          <div>
            <span className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide">Rating</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                  title={`${n} star${n > 1 ? 's' : ''}`}
                  className="p-0.5"
                >
                  <FiStar
                    className={`w-5 h-5 ${
                      n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-xs text-slate-500">{form.rating} / 5</span>
            </div>
          </div>
          <div>
            <label
              htmlFor="testimonial-feedback"
              className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide"
            >
              Feedback<span className="text-rose-500 ml-1">*</span>
            </label>
            <textarea
              id="testimonial-feedback"
              rows={4}
              value={form.feedback}
              onChange={(e) => setForm((f) => ({ ...f, feedback: e.target.value }))}
              placeholder="What the patient said"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.showOnWebsite}
              onChange={(e) => setForm((f) => ({ ...f, showOnWebsite: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-sky-600"
            />
            Show on website
          </label>
        </form>
      </Modal>
    </div>
  );
}
